import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  IAgreedTermsConditions,
  ICreateTermsConditionsVersion,
  ITermsConditionsAudienceType,
  ITermsConditionsVersion,
} from '../interfaces/terms-conditions-version';
import { ITermsConditionsVersionFilter } from '../interfaces/terms-conditions-version-filter';
import { ReadService } from './read.service';
import { HttpResponsesHandlersFactory } from '../handlers/http-responses-handler';
import FileSaver from 'file-saver';
import { MessageService } from 'primeng';
import { Message } from '../classes/Message';
import { UserService } from './user.service';

@Injectable({ providedIn: 'root' })
export class TermsConditionsService extends ReadService<
  ITermsConditionsVersion,
  ITermsConditionsVersionFilter
> {
  url = '/api/termsconditions';
  entityTitle = 'Terms & conditions version';

  constructor(
    http: HttpClient,
    private userService: UserService,
    handlersFactory: HttpResponsesHandlersFactory,
    private messageService: MessageService,
  ) {
    super(http, handlersFactory);
  }

  public getDownloadPdfURL(path: string) {
    return `${this.url}/downloadpdf?path=${path}`;
  }

  public downloadPdf(path: string) {
    this.http.get(this.getDownloadPdfURL(path), { params: null, responseType: 'blob' }).subscribe(
      (x) => this.handleDownloadPdf(x),
      (error) => {
        this.messageService.add(Message.Error('Impossible to download the PDF.'));
      },
      () => {
        this.messageService.add(
          Message.Success('Downloaded succesful. Check your download folder.'),
        );
      },
    );
  }

  public handleDownloadPdf(blob) {
    let filename = 'terms-conditions.pdf';
    FileSaver.saveAs(blob, filename);
  }

  public downloadTermsOfUse() {
    // Fetch the Url of the organisations Terms
    this.getCurrentTermsOfUse().then((abc) => {
      if (abc == null || abc.length == 0) {
        this.messageService.add(
          Message.Error(
            '<div>Terms & Conditions could not be downloaded.</div><div>Please call Engineius Support</div>',
          ),
        );
      } else {
        // Download the PDF
        this.downloadPdf(abc[0].documentUrl);
      }
    });
  }

  saveVersion(termsConditionsVersion: ICreateTermsConditionsVersion, isNew: boolean): Promise<any> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json;charset=utf-8' });
    let successMsg = isNew ? this.entityTitle + ' added.' : this.entityTitle + ' updated.';

    return this.handleRequest(
      this.http.post(`${this.url}/saveVersion`, termsConditionsVersion, { headers: headers }),
      this.handlersFactory.getForUpdate(this.entityTitle),
      successMsg,
    );
  }

  agreeTermsConditions(agreedVersionId: number): Promise<any> {
    return this.http
      .post<any>(`${this.url}/${agreedVersionId}/agree`, {})
      .toPromise()
      .catch(this.handleError);
  }

  public getCurrentlyAgreedTermsConditionsByOrg(orgId): Promise<IAgreedTermsConditions> {
    return this.http
      .get<IAgreedTermsConditions>(`${this.url}/${orgId}/currently-agreed`)
      .toPromise()
      .then(this.extractData)
      .catch(this.handleError);
  }

  public getCurrentlyAgreedTermsConditions(): Promise<IAgreedTermsConditions> {
    return this.http
      .get<IAgreedTermsConditions>(`${this.url}/currently-agreed`)
      .toPromise()
      .then(this.extractData)
      .catch(this.handleError);
  }

  public getCurrentAgreement(): Promise<ITermsConditionsVersion[]> {
    return this.http
      .get<any>(`${this.url}/getCurrentAgreement`)
      .toPromise()
      .then(this.extractData)
      .catch(this.handleError);
  }

  public getCurrentTermsOfUse(): Promise<ITermsConditionsVersion[]> {
    return this.http
      .get<any>(`${this.url}/getCurrentTermsOfUse`)
      .toPromise()
      .then(this.extractData)
      .catch(this.handleError);
  }

  public getAudienceTypes(): Promise<ITermsConditionsAudienceType[]> {
    return this.http
      .get<any>(`${this.url}/getAudienceTypes`)
      .toPromise()
      .then(this.extractData)
      .catch(this.handleError);
  }
}
